import { UserQuery } from "#src/api/User.ts";
import UserAvatar from "#src/components/layout/UserAvatar.tsx";
import Modal from "#src/components/modals/Modal.tsx";
import { LevelTag } from "#src/components/user/LevelTag.tsx";
import UserModel from "#src/models/UserModel.ts";
import { apiClient, useInfiniteApi } from "#src/stores/Api.tsx";
import { useStore } from "#src/stores/Store.tsx";
import message from "#src/utils/message.tsx";
import { modals } from "#src/utils/modals.ts";
import { useDebounce } from "#src/utils/useDebounce.ts";
import { useFakeInput } from "#src/utils/useFakeInput.ts";
import {
  Level,
  SortOrder,
  filteredUsersVariables,
  modalUsers,
  modalUsersVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { useModal } from "@ebay/nice-modal-react";
import { groupBy } from "es-toolkit/compat";
import { gql } from "graphql-tag";
import { useEffect, useRef, useState } from "react";
import { InputText } from "../inputs/InputText";
import StudentAge from "../items/StudentAge";
import List from "../layout/List";

interface Props {
  levels?: Level[];
  handleOk: (userProfile: UserModel) => void;
  helpText?: string;
  user?: UserModel;
  title?: string;
  idNot?: string[];
  primaryOnly?: boolean;
}

export const FilterUsersModal = modals.create((props: Props) => {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const store = useStore();
  const searchValue = (value || "").trim();
  const debouncedValue = useDebounce(searchValue, { wait: 1000 });
  const searchPending = searchValue !== debouncedValue;
  const searchTooShort = searchValue.length > 0 && searchValue.length < 3;
  const skipSearch = searchPending || searchTooShort;
  const modal = useModal();
  const fakeInput = useFakeInput();

  const variables: filteredUsersVariables = {
    where: {
      level: {
        in: props.levels,
      },
      id: props.idNot
        ? {
            notIn: props.idNot,
          }
        : undefined,
    },
    skip: 0,
    take: 10,
    orderBy: {
      lastName: SortOrder.asc,
    },
    filter:
      debouncedValue.length >= 3
        ? `?${new URLSearchParams({ search: debouncedValue })}`
        : "",
  };

  if (!store.isSuperAdminArea) {
    variables.where.organizationId = store.organization.id;
  }

  if (props.user) {
    variables.where.id = {
      not: props.user.id,
    };
  }

  if (props.primaryOnly) {
    variables.where.primaryAccountId = {
      equals: null,
    };
  }

  const getUserData = useInfiniteApi(ModalUsersQuery, {
    variables,
    skip: skipSearch,
  });
  const paginationRef = useRef({ getUserData, skipSearch });
  paginationRef.current = { getUserData, skipSearch };

  const users = skipSearch ? [] : getUserData?.users || [];

  const groupedUsers = groupBy(users, (user) => {
    if (user.primaryAccount) {
      return user.primaryAccount.id;
    }

    if (user.accountOwner) {
      return user.accountOwner.id;
    }

    return user.id;
  });

  for (const key in groupedUsers) {
    const group = groupedUsers[key];

    const sortedGroup = group.sort((a, b) => {
      if (a.level !== Level.Student && !a.primaryAccount) {
        return -1;
      }

      if (a.level === Level.Student && b.level === Level.Student) {
        return a.firstName.localeCompare(b.firstName);
      }

      return 0;
    });

    groupedUsers[key] = sortedGroup;
  }

  const sortedUsers = Object.values(groupedUsers).flat();

  useEffect(() => {
    if (modal.visible && fakeInput && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      fakeInput.remove();
    }
  }, [modal.visible, inputRef.current]);

  function handleChange(e) {
    const newValue = e;

    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0 });
    }

    if (newValue !== value) {
      setValue(newValue);
    }
  }

  function onEndReached() {
    const { getUserData, skipSearch } = paginationRef.current;
    if (skipSearch || !getUserData?.hasNextPage || getUserData.isFetching) {
      return;
    }
    void getUserData.fetchNextPage();
  }

  async function clearValue() {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0 });
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }

    setValue("");
  }

  async function selectUser(user) {
    const processingMessage: any = message.loading("Processing…");

    const userData = await apiClient(UserQuery, {
      variables: {
        id: user.id,
      },
    }).then((res) => new UserModel(res.user));

    processingMessage();

    props.handleOk(userData);
    void modal.hide();
  }

  function renderUser(item, index) {
    const user = new UserModel(item);
    const name = `${user.firstName} ${user.lastName}`;
    let students = user.students;

    if (user.primaryAccount) {
      students = user.primaryAccount.students;
    }

    return (
      <div
        key={index}
        className="border-borderGray hover:bg-bgGray flex cursor-pointer gap-4 border-b px-4 py-3 transition"
        onClick={() => selectUser(user)}
      >
        {user.level === Level.Student && (
          <div>
            <UserAvatar user={user} small showInitials />
          </div>
        )}

        <div className="flex-1">
          <LevelTag user={user} />

          {user.accountOwner && (
            <div className="step-subhead">
              {user.accountOwner.lastName} Account
            </div>
          )}

          <div
            className="text-organization leading-tight"
            dangerouslySetInnerHTML={{
              __html: name.replace(
                new RegExp(debouncedValue, "gi"),
                (match) => `<strong>${match}</strong>`
              ),
            }}
          />

          {user.email && (
            <div
              className="text-sm"
              dangerouslySetInnerHTML={{
                __html: user.email.replace(
                  new RegExp(debouncedValue, "gi"),
                  (match) => `<strong>${match}</strong>`
                ),
              }}
            />
          )}

          {students.length > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <div>
                <i className="far fa-user text-xs" />
              </div>
              <div className="flex-1">
                {students.map((s) => `${s.firstName} ${s.lastName}`).join(", ")}
              </div>
            </div>
          )}

          <div className="text-sm">
            {user.birthDate && <StudentAge student={user} />}{" "}
            {user.birthDate && user.gender && " • "}{" "}
            {user.gender ? user.gender : ""}
          </div>
        </div>
      </div>
    );
  }

  function renderUsers() {
    return (
      <div
        style={{
          height: props.helpText
            ? `calc(100vh - 209px)`
            : `calc(100vh - 189px)`,
        }}
      >
        <List
          rows={sortedUsers}
          renderRow={renderUser}
          onEndReached={onEndReached}
          scrollOffset={100}
          loading={getUserData.loading || searchPending}
          emptyMessage={
            searchTooShort
              ? "Search requires at least 3 characters."
              : "No users found matching that term."
          }
        />
      </div>
    );
  }

  return (
    <Modal
      visible={modal.visible}
      handleCancel={modal.hide}
      afterClose={modal.remove}
      title={props.title || "Select User"}
      // initialFocus={inputRef}
    >
      <div className="-mx-4 -my-4">
        <div className="px-4 pt-4" style={{ height: props.helpText ? 90 : 60 }}>
          {props.helpText && <p className="mb-3">{props.helpText}</p>}
          <div className="relative">
            <InputText
              placeholder="Search"
              id="search"
              value={value}
              autoFocus
              onChange={handleChange}
              ref={inputRef}
              className="rounded-full"
              loading={getUserData.isFetching}
              allowClear
              onClear={clearValue}
            />
          </div>
        </div>
        <hr className="mb-0" />
        {renderUsers()}
      </div>
    </Modal>
  );
});

export const ModalUsersQuery = gql`
  query modalUsers(
    $where: UserWhereInput!
    $orderBy: UserOrderByInput
    $filter: String
    $skip: Int
    $take: Int
  ) {
    users(
      where: $where
      orderBy: $orderBy
      filter: $filter
      take: $take
      skip: $skip
      skipCount: true
    ) {
      id
      accountOwner {
        id
        email
        firstName
        lastName
        phone
        linkedAccounts {
          id
          firstName
          lastName
        }
      }
      avatar {
        id
        url
      }
      phone
      primaryAccount {
        id
        students {
          id
          firstName
          lastName
        }
      }
      email
      firstName
      lastName
      birthDate
      level
      gender
      stripeCustomerId
      stripeToken
      adyenToken
      students {
        id
        firstName
        lastName
      }
    }
  }
` as TypedDocumentNode<modalUsers, modalUsersVariables>;
